import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useKaannos } from '../lib/i18n';
import { kentta, nappiEnsisijainen, nappiToissijainen } from '../lib/tyylit';

/**
 * Oman salasanan vaihto (suunnitelma 13.2.1).
 *
 * Sama lomake kahdessa paikassa: vapaaehtoisena ylätunnisteen dialogissa ja
 * pakotettuna silloin, kun ylläpitäjä on asettanut väliaikaisen salasanan.
 * Ero on vain kehyksessä, joten lomake on oma komponenttinsa.
 *
 * Vahvistuskenttä on selaimen puolella eikä skeemassa: se ei ole sääntö
 * salasanasta vaan suoja kirjoitusvirheeltä, eikä palvelimen tarvitse
 * tietää siitä.
 */
export default function SalasananVaihto({
  onValmis,
  onPeruuta,
}: {
  onValmis?: () => void;
  onPeruuta?: () => void;
}) {
  const { t } = useKaannos();
  const queryClient = useQueryClient();
  const [nykyinen, setNykyinen] = useState('');
  const [uusi, setUusi] = useState('');
  const [uusiUudelleen, setUusiUudelleen] = useState('');
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lahetetaan, setLahetetaan] = useState(false);

  async function laheta(event: React.FormEvent) {
    event.preventDefault();
    setVirhe(null);

    if (uusi !== uusiUudelleen) {
      setVirhe(t('salasana.eiTasmaa'));
      return;
    }

    setLahetetaan(true);
    try {
      await api.post('/api/me/password', { currentPassword: nykyinen, newPassword: uusi });
      // Istunnon lippu `mustChangePassword` muuttui palvelimella, joten
      // koko välimuisti on haettava uudelleen — muuten pakko jäisi
      // näkyviin, vaikka se on jo purettu.
      await queryClient.invalidateQueries();
      onValmis?.();
    } catch (error) {
      setVirhe(error instanceof ApiError ? error.message : t('yleinen.tallennusEpaonnistui'));
    } finally {
      setLahetetaan(false);
    }
  }

  return (
    <form onSubmit={laheta} className="space-y-3">
      <label className="block">
        <span className="text-teksti mb-1 block text-sm font-medium">{t('salasana.nykyinen')}</span>
        <input
          type="password"
          required
          autoComplete="current-password"
          value={nykyinen}
          onChange={(event) => setNykyinen(event.target.value)}
          className={kentta}
        />
      </label>

      <label className="block">
        <span className="text-teksti mb-1 block text-sm font-medium">{t('salasana.uusi')}</span>
        <input
          type="password"
          required
          minLength={12}
          autoComplete="new-password"
          value={uusi}
          onChange={(event) => setUusi(event.target.value)}
          className={kentta}
        />
        <span className="text-teksti-vaimea mt-1 block text-xs">{t('salasana.vaatimus')}</span>
      </label>

      <label className="block">
        <span className="text-teksti mb-1 block text-sm font-medium">
          {t('salasana.uusiUudelleen')}
        </span>
        <input
          type="password"
          required
          autoComplete="new-password"
          value={uusiUudelleen}
          onChange={(event) => setUusiUudelleen(event.target.value)}
          className={kentta}
        />
      </label>

      {virhe && (
        <p
          role="alert"
          className="border-yli/40 bg-yli/10 text-teksti rounded-md border px-3 py-2 text-sm"
        >
          {virhe}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        {onPeruuta && (
          <button type="button" onClick={onPeruuta} className={nappiToissijainen}>
            {t('yleinen.peruuta')}
          </button>
        )}
        <button type="submit" disabled={lahetetaan} className={nappiEnsisijainen}>
          {lahetetaan ? t('yleinen.tallennetaan') : t('salasana.vaihda')}
        </button>
      </div>
    </form>
  );
}
